import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { PastTeamSummary } from "@/api/registrations";
import type { TeamChoice } from "./types";
import { hasNonLatin } from "@/lib/latinName";

interface Props {
  pastTeams: PastTeamSummary[];
  value: TeamChoice;
  onChange: (next: TeamChoice) => void;
  /** Unique prefix for RadioGroupItem ids so two choosers on one page don't collide. */
  idPrefix?: string;
  /** Show a "Your previous teams" label above the radio list. */
  showLabel?: boolean;
  autoFocusNameInput?: boolean;
  /** Inline validation message (e.g. empty team name after a submit attempt). */
  error?: string | null;
}

/** Past-teams radio list + "create new" name input. Used wherever the user
 *  picks an existing team or names a new one. */
export function TeamChooser({
  pastTeams,
  value,
  onChange,
  idPrefix = "team",
  showLabel = false,
  autoFocusNameInput = true,
  error = null,
}: Props) {
  return (
    <div className="space-y-2">
      {pastTeams.length > 0 && (
        <>
          {showLabel && (
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Your previous teams
            </Label>
          )}
          <RadioGroup
            value={value.kind === "existing" ? String(value.teamId) : "__new__"}
            onValueChange={(v) => {
              if (v === "__new__") onChange({ kind: "new", name: "" });
              else onChange({ kind: "existing", teamId: Number(v) });
            }}
            className="space-y-1"
          >
            {pastTeams.map((t) => (
              <label
                key={t.team_id}
                className="flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer hover:bg-background"
              >
                <RadioGroupItem
                  value={String(t.team_id)}
                  id={`${idPrefix}-${t.team_id}`}
                />
                <span className="text-sm">{t.team_name}</span>
              </label>
            ))}
            <label className="flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer hover:bg-background">
              <RadioGroupItem value="__new__" id={`${idPrefix}-new`} />
              <span className="text-sm">Create a new team…</span>
            </label>
          </RadioGroup>
        </>
      )}
      {(value.kind === "new" || pastTeams.length === 0) && (
        <>
          <Input
            placeholder={pastTeams.length === 0 ? "Team name (e.g. Quiz Wizards)" : "New team name"}
            value={value.kind === "new" ? value.name : ""}
            onChange={(e) => onChange({ kind: "new", name: e.target.value })}
            autoFocus={autoFocusNameInput}
          />
          {value.kind === "new" && hasNonLatin(value.name) ? (
            <p className="text-xs text-destructive">
              Please use Latin letters for the team name.
            </p>
          ) : error ? (
            <p className="text-xs text-destructive">{error}</p>
          ) : null}
        </>
      )}
    </div>
  );
}
